
import { DiaryEntry } from '../types';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { entryToSupabaseFormat, supabaseToEntryFormat } from '../transformers';
import { handleError } from '../errorHandling';
import { saveLocalEntry, getLocalEntries, getLocalUserEntries } from './localStorage';

/**
 * Save a diary entry to Supabase and localStorage as fallback
 */
export async function saveEntry(entry: DiaryEntry): Promise<void> {
  if (!entry.userId) {
    console.error('Attempted to save entry without userId');
    return;
  }
  
  try {
    const supabaseEntry = entryToSupabaseFormat(entry);
    const { error } = await supabase
      .from('diary_entries')
      .insert(supabaseEntry);
      
    if (error) throw error;
    saveLocalEntry(entry);
  } catch (error) {
    handleError(error, "Failed to save diary entry");
  }
}

/**
 * Get all diary entries from Supabase, fallback to localStorage
 */
export async function getEntries(): Promise<DiaryEntry[]> {
  try {
    const { data, error } = await supabase
      .from('diary_entries')
      .select('*')
      .order('created_at', { ascending: false });
      
    if (error) throw error;
    
    if (data && data.length > 0) {
      return data.map(entry => supabaseToEntryFormat(entry));
    }
    
    return getLocalEntries();
  } catch (error) {
    handleError(error, "Error fetching diary entries");
    return getLocalEntries();
  }
}

/**
 * Get entries for a specific user
 */
export async function getUserEntries(userId: string): Promise<DiaryEntry[]> {
  if (!userId) {
    console.warn('Attempted to get user entries without userId');
    return [];
  }
  
  try {
    const { data, error } = await supabase
      .from('diary_entries')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
      
    if (error) throw error;
    
    if (data && data.length > 0) {
      return data.map(entry => supabaseToEntryFormat(entry));
    }
    
    return getLocalUserEntries(userId);
  } catch (error) {
    handleError(error, "Error fetching user diary entries");
    return getLocalUserEntries(userId);
  }
}
